<template>
	<main class="account-plan">
		<AccountPlanHeader :template-name="templateName"
			:has-repeated-plan-account="has_repeated_plan_account"
			:needs-review="!!(basic_data && basic_data.needs_account_plan_review)"
			:uploading="isUploadingPlan"
			@file-selected="uploadFilePlanAccounts" @export-accounts="downloadAllPlanAccounts"
			@make-unique="makeAccountPlansUnique" @approve-review="approveAccountPlanReview" />
		<b-loading :is-full-page="false" :active="isUploadingPlan"></b-loading>
		<p class="ap-subtitle">Cuentas {{ consolidatedBusinessType }}</p>

		<div class="px-4 mb-2 is-hidden-mobile">
			<div class="columns is-vcentered ap-colheads">
				<div class="column is-1 has-text-right">Nro.</div>
				<div class="column is-2">Nivel 1</div>
				<div class="column is-2">Nivel 2</div>
				<div class="column is-3">Nivel 3 / Cuenta</div>
				<div class="column is-2">Cuenta X</div>
				<div class="column is-1">Orden</div>
				<div class="column is-1 has-text-centered">Operaciones</div>
			</div>
		</div>

		<div v-for="(item, index) in account_plans" :key="item.id"
			:class="['ap-row', rowBackgrounds[index], { 'ap-row--editing': indexEditing == index }]">
			<!-- Mientras se edita, el formulario TOMA EL LUGAR de la fila (misma
				grilla, campos bajo sus columnas) en vez de duplicarla debajo. -->
			<AccountPlanRow v-if="indexEditing != index" :item="item" :index="index"
				show-balance-concept
				@edit-account="initializeEditingPlanAccount(index)"
				@delete-account="openConfirmRestoreModal('delete', item.id)" />

			<AccountPlanInlineForm v-else :level1-values="level_1_values"
				:level2-options="level_2_values_depending" :balance-concepts="balance_concepts"
				:level1.sync="level1Editing" :level2.sync="level2Editing"
				:level3.sync="level3Editing" :order.sync="orderEditing"
				:balance-concept.sync="balanceConceptEditing"
				submit-label="OK" submit-class="theme_btn_dark" level2-remount cancellable
				@level1-change="onLevel1Change" @cancel="indexEditing = -1"
				@submit="openConfirmRestoreModal('edit', item.id)" />
		</div>

		<div class="ap-row ap-row--new">
			<AccountPlanInlineForm :level1-values="level_1_values"
				:level2-options="level_2_values_depending" :balance-concepts="balance_concepts"
				:level1.sync="new_level_1_value" :level2.sync="new_level_2_value"
				:level3.sync="newAccount" :order.sync="new_order"
				:balance-concept.sync="newBalanceConcept"
				submit-label="+" submit-class="theme_btn_blue" @level1-change="onLevel1Change"
				@submit="addAccount" />
		</div>
	</main>
</template>

<script>
import { mapActions } from "vuex";
import AccountPlanHeader from "@/components/companyInfo/AccountPlanHeader";
import AccountPlanInlineForm from "@/components/companyInfo/AccountPlanInlineForm";
import AccountPlanRow from "@/components/companyInfo/AccountPlanRow";
import ConfirmRestore from "@/components/modals/ConfirmRestore";
import {
	consoleHandlerError,
	extractErrorMessage,
	notificate,
} from "@/utils/message";

export default {
	name: "CompanyInfoAccountPlan",
	props: {
		basicData: {
			type: Object,
			required: true,
			default: () => ({}),
		},
	},
	components: {
		AccountPlanHeader,
		AccountPlanInlineForm,
		AccountPlanRow,
	},
	mounted() {
		if (this.basic_data && this.basic_data.companyId) {
			this.loadDefaultPlans()
		}
	},
	data() {
		return {
			consolidatedBusinessType: "_",
			isUploadingPlan: false,
			basic_data: {},
			account_plans: [],
			new_level_1_value: "",
			new_level_2_value: "",
			level_1_values: [],
			level_2_values: {},
			level_2_values_depending: [],
			newAccount: "",
			// Vacío = sin orden explícito: el backend la deja última en su
			// categoría (next_order_in_category).
			new_order: "",
			newBalanceConcept: "",
			balance_concepts: [],
			level1Editing: "",
			level2Editing: "",
			level3Editing: "",
			orderEditing: "",
			balanceConceptEditing: "",
			templateName: "",
			has_repeated_plan_account: false,
			indexEditing: -1,
			modalDefault: {
				parent: this,
				width: "25%",
				hasModalCard: true,
				canCancel: ["escape", "outside"],
				customClass: "center-always-modal",
			},
		};
	},
	computed: {
		rowBackgrounds() {
			// Franjas por grupos de level_2 consecutivos, en una sola pasada.
			let groupIndex = 0;
			return this.account_plans.map((plan, i) => {
				if (i > 0 && plan.level_2 !== this.account_plans[i - 1].level_2) {
					groupIndex++;
				}
				return groupIndex % 2 === 0 ? "bg-group-a" : "bg-group-b";
			});
		},
	},
	methods: {
		...mapActions("analystCompany", [
			"approveAccountPlan",
			"createAccount",
			"deleteAccount",
			"editAccount",
			"fetchAccountPlans",
			"makeUniquePlanAccounts",
			"uploadAllPlanAccounts",
		]),
		approveAccountPlanReview() {
			// Visto bueno del analista: baja el banner "En revisión" del cliente.
			this.approveAccountPlan(this.basic_data.companyId)
				.then(() => {
					this.basic_data.needs_account_plan_review = false;
					notificate("Visto bueno dado al plan de cuentas.");
				})
				.catch((error) => {
					consoleHandlerError({ error });
				});
		},
		initializeEditingPlanAccount(iPlan) {
			this.indexEditing = iPlan;
		},
		onLevel1Change(val) {
			this.level_2_values_depending = this.level_2_values[val]["items"];
		},
		async addAccount() {
			if (
				!this.basic_data.companyId ||
				!this.new_level_2_value ||
				!this.newAccount
			) {
				notificate("Debe llenar los datos.");
				return;
			}
			try {
				const data = await this.createAccount({
					companyId: this.basic_data.companyId,
					level2Id: this.new_level_2_value,
					accountName: this.newAccount,
					order: this.new_order,
					balanceConcept: this.newBalanceConcept,
				});
				notificate(
					data.status || "Cuenta grabada exitosamente.",
					"is-success is-light",
				);
				this.new_level_2_value = "";
				this.newAccount = "";
				this.new_level_1_value = "";
				this.new_order = "";
				this.newBalanceConcept = "";
				await this.loadDefaultPlans();
			} catch (error) {
				// El backend ahora devuelve 400/404/409 con {detail} en los casos
				// de negocio (falta nivel 2, duplicada, datos incompletos).
				notificate(
					extractErrorMessage(error, "No se pudo crear la cuenta."),
					"is-danger is-light",
				);
				consoleHandlerError({ error, silent: true });
			}
		},
		async editPlanAccount(idPlanAccount) {
			if (
				!this.basic_data.companyId ||
				!this.level2Editing ||
				!this.level3Editing ||
				!this.orderEditing
			) {
				notificate("Debe llenar todos los datos.");
				return;
			}
			try {
				const data = await this.editAccount({
					companyId: this.basic_data.companyId,
					accountId: idPlanAccount,
					level2Id: this.level2Editing,
					accountName: this.level3Editing,
					order: this.orderEditing,
					// "" viaja como null y limpia: el form refleja el estado final.
					balanceConcept: this.balanceConceptEditing,
				});
				if (data["status"] == "Ok") {
					notificate("Cuenta grabada exitosamente.");
				} else {
					notificate(data["status"]);
				}

				this.level1Editing = "";
				this.level2Editing = "";
				this.level3Editing = "";
				this.orderEditing = "";
				this.balanceConceptEditing = "";
				this.indexEditing = -1;
				await this.loadDefaultPlans();
			} catch (error) {
				consoleHandlerError({ error, empty: true });
			}
		},
		async deletePlanAccount(idPlanAccount) {
			try {
				await this.deleteAccount({
					companyId: this.basic_data.companyId,
					accountId: idPlanAccount,
				});
				notificate("Plan de cuenta borrado");
			} catch (err) {
				notificate("Ocurrio un error");
			} finally {
				await this.loadDefaultPlans();
			}
		},
		async loadDefaultPlans() {
			if (this.basic_data && this.basic_data.companyId) {
				const data = await this.fetchAccountPlans(
					this.basic_data.companyId,
				);
				this.level_1_values = data["level_1_values"];
				this.level_2_values = data["level_2_values"];
				this.balance_concepts = data["balance_concepts"] || [];
				this.account_plans = data["account_plans"];
				this.has_repeated_plan_account =
					data["has_repeated_plan_account"];
				if (!this.basic_data.classify_by_sales) {
					this.account_plans = this.account_plans.filter(
						(elem) => elem.level_1 != "Ventas",
					);
					this.level_1_values = this.level_1_values.filter(
						(elem) => elem.name != "Ventas",
					);
				}
				this.consolidatedBusinessType =
					data["tipo_negocio_consolidado"];
			}
		},
		makeAccountPlansUnique() {
			this.makeUniquePlanAccounts(this.basic_data.companyId).then(() => {
				this.loadDefaultPlans();
			});
		},
		async downloadAllPlanAccounts() {
			// Carga la librería dinámicamente solo cuando se necesita
			const XLSX = await import('xlsx');
			let data = []
			this.account_plans.map((plan) => {
				data.push({
					"Nivel 1": plan.level_1,
					"Nivel 2": plan.level_2,
					"Nivel 3": plan.level_3,
				})
			});


			// Crear hoja y libro de trabajo
			const worksheet = XLSX.utils.json_to_sheet(data);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, "Plan de cuentas");

			// Generar y descargar el archivo
			XLSX.writeFile(workbook, "Plan de cuentas.xlsx");
		},
		async uploadFilePlanAccounts(file) {
			// El import reemplaza el plan completo: un segundo submit en vuelo
			// competiría con el borrado/creación del primero.
			if (this.isUploadingPlan) return;
			const formData = new FormData();
			formData.append("planAccountsFile", file);

			this.isUploadingPlan = true;
			try {
				const data = await this.uploadAllPlanAccounts({
					companyId: this.basic_data.companyId,
					formData,
				});
				if (data.result) {
					throw new Error(data.result)
				}
				// El overlay sigue activo hasta que la tabla recargó: el éxito
				// se notifica con el plan nuevo ya visible.
				await this.loadDefaultPlans();
				notificate(
					`<strong>Subido con éxito</strong>
					<br>el archivo se subio correctamente`,
					"is-success is-light"
				);
			} catch (err) {
				// El backend responde 400 con el detalle en `result`
				// (catálogo/columnas/archivo); sin payload cae al error
				// genérico de axios.
				const detail = err?.response?.data?.result ?? err;
				notificate(
					`<strong>Error</strong>
					<br>hubo un problema con ${detail}`,
					"is-danger is-light"
				);
			} finally {
				this.isUploadingPlan = false;
			}
		},
		openConfirmRestoreModal(type, idPlanAccount) {
			let title = 'eliminar'
			let subTitle = 'Esta acción revertirá todos los cambios en prorratas y devengo'
			if (type == 'edit') {
				title = 'editar'
				subTitle = 'Esta acción cambiara la clasificación de todos los documentos asociados a esta cuenta'
			}
			this.$buefy.modal.open({
				...this.modalDefault,
				component: ConfirmRestore,
				props: {
					eventNameConfirmRestore: 'confirmEvent',
					tittleRestore: '¿Desea ' + title + ' esta cuenta?',
					subTittleRestore: subTitle,
					msgOkAction: title.charAt(0).toUpperCase() + title.slice(1)
				},
				events: {
					confirmEvent: () => {
						if (type == 'delete') {
							this.deletePlanAccount(idPlanAccount)
						} else if (type == 'edit') {
							this.editPlanAccount(idPlanAccount)
						}
					}
				},
			});
		},
	},
	watch: {
		basicData: {
			handler(data) {
				if (data && data.companyId) {
					this.basic_data = data;
					this.loadDefaultPlans();
					Object.keys(this.basic_data.business_types).map(item => {
						if (item == 'Servicios') {
							this.templateName = 'Plan de cuentas - Servicios'
						} else {
							this.templateName = 'Plan de cuentas - Productor'
						}
					})
				}
			},
			immediate: true,
		},
		indexEditing(newVal) {
			// newVal puede ser 0 (primera fila): comparar contra -1, no truthiness.
			if (newVal !== null && newVal >= 0) {
				this.level1Editing = this.account_plans[newVal].level_1;
				this.level3Editing = this.account_plans[newVal].level_3;
				this.orderEditing = this.account_plans[newVal].order;
				this.balanceConceptEditing =
					this.account_plans[newVal].balance_concept || "";
				this.onLevel1Change(this.level1Editing);
				this.level2Editing = this.level_2_values_depending.find(
					(item) => item.level_2 == this.account_plans[newVal].level_2,
				).id;
			}
		},
	},
};
</script>
<style scoped lang="scss">
.account-plan {
	/* ancla del <b-loading :is-full-page="false"> del import */
	position: relative;
	font-family: var(--ap-font);
	color: var(--ap-ink-2);
}

.ap-subtitle {
	text-align: center;
	font-weight: 600;
	color: var(--ap-slate);
	margin-bottom: 1.5rem;
}

// ── Column headers ──────────────────────────────────────────────────────
.ap-colheads .column {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: var(--ap-muted);
}

// ── Rows ────────────────────────────────────────────────────────────────
.ap-row {
	padding: 0.5rem 0.75rem;
	margin-bottom: 0.25rem;
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-sm);
	transition: border-color 0.2s var(--ap-ease), box-shadow 0.2s var(--ap-ease);

	&:hover {
		border-color: var(--ap-line-strong);
	}

	&--new {
		background-color: var(--ap-mist);
		border-style: dashed;
	}

	// Fila en edición: el acento marca DÓNDE se está editando sin gritar.
	&--editing {
		border-color: var(--ap-teal);
		box-shadow: 0 0 0 3px var(--ap-teal-wash);
	}
}

// Franjas suaves por grupo de level_2 (clases calculadas en rowBackgrounds)
.bg-group-a {
	background-color: var(--ap-teal-wash);
}

.bg-group-b {
	background-color: var(--ap-paper);
}
</style>
